import {
    Box,
    Button,
    CircularProgress, Container,
    CssBaseline,
    Drawer,
    LinearProgress,
    AppBar as MuiAppBar, Toolbar, Typography,
    useMediaQuery, useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAllParticipants, getParticipantByIndex, getParticipantStepCount } from '../utils/booking';
import { ApiContext } from './ApiProvider';
import LanguageSelector from './LanguageSelector';
import Sidebar from './Sidebar';

import ParticipantStep from './ParticipantStep';
import PreCheckDisabled from './PreCheckDisabled';
import PreCheckinStep from './PreCheckinStep';
import SuccessStep from './SuccessStep';
import { TourOperatorBrand } from './TourOperatorBrand';

const drawerWidth = 290;
const appBarHeight = '64px';

const MainContent = styled('main')(
    ({ theme }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        boxShadow: theme.shadows[2],
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        alignSelf: 'flex-start',
    }),
);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(
    ({ theme, open }) => ({
        zIndex: theme.zIndex.drawer + 1,
    })
);

// Wrapper component to handle step logic and validation
const StepWrapper = ({ children, requiredStepIndex, totalSteps }) => {
    const navigate = useNavigate();
    const { booking, preCheckSettings } = useContext(ApiContext);
    const [isAllowed, setIsAllowed] = useState(null); // null: checking, true: allowed, false: redirect
    const location = useLocation(); // Get location for redirect check

    useEffect(() => {
        if (booking) {
            const participantSteps = getParticipantStepCount(booking, preCheckSettings);
            const preCheckinIndex = participantSteps;
            const successIndex = preCheckinIndex + 1; // Conceptual index for routing/steps

            let allowed = false;
            const isParticipantStep = requiredStepIndex >= 0 && requiredStepIndex < preCheckinIndex;
            const isPreCheckinStep = requiredStepIndex === preCheckinIndex;
            const isSuccessStep = requiredStepIndex === successIndex;

            // Rule 1: Success step - allowing direct access
            if (isSuccessStep) {
                allowed = true;
            }
            // Rule 2: Participant steps are always allowed now
            else if (isParticipantStep) {
                allowed = true;
            }
            // Rule 3: Pre-checkin step is always accessible (validation happens inside the component)
            else if (isPreCheckinStep) {
                allowed = true;
            }

            // General validation: Disallow invalid indices outside the known range [0, successIndex]
            if (requiredStepIndex < 0 || requiredStepIndex > successIndex) {
                console.warn(`Attempted to access invalid step index: ${requiredStepIndex}`);
                allowed = false;
            }

            if (allowed) {
                setIsAllowed(true);
            } else {
                // Redirect to first participant step as default
                const redirectTo = '/participant/0';

                console.debug(`Redirecting: Step ${requiredStepIndex} not allowed. Redirecting to ${redirectTo}`);
                // Avoid infinite redirect loops if already at the destination but still disallowed
                if (location.pathname !== redirectTo) {
                    navigate(redirectTo, { replace: true });
                    // Set allowed to false immediately to prevent rendering during navigation
                    setIsAllowed(false);
                } else {
                    // If already at the target but logic still denies access, prevent rendering.
                    console.warn(`Already at redirect target ${redirectTo}, but access still denied. Preventing render.`);
                    setIsAllowed(false);
                }
            }
        }
    }, [booking, requiredStepIndex, totalSteps, navigate, location.pathname, preCheckSettings]);

    if (isAllowed === null) {
        return null; // Still determining access
    }

    if (!isAllowed) {
        return null; // Access denied and redirect in progress
    }

    return children;
};

const ParticipantRoute = ({ totalSteps, handleSaveAndContinue }) => {
    const { participantIndex: indexStr } = useParams();
    const { booking, preCheckSettings } = useContext(ApiContext);
    const participantIndex = parseInt(indexStr, 10);

    if (isNaN(participantIndex) || !booking || participantIndex >= getParticipantStepCount(booking, preCheckSettings)) {
        console.debug("Invalid participant index in Route, redirecting.");
        // Redirect to the first valid step if the index is bad
        return <Navigate to="/participant/0" replace />;
    }

    const participant = getParticipantByIndex(booking, participantIndex);
    const isReadOnly = participant?.status === 'Pre-Checked-in';

    return (
        <StepWrapper requiredStepIndex={participantIndex} totalSteps={totalSteps}>
            <ParticipantStep
                key={`participant-${participantIndex}`} // More specific key
                participantIndex={participantIndex}
                onSaveAndContinue={handleSaveAndContinue}
                isReadOnly={isReadOnly}
            />
        </StepWrapper>
    );
};

const PreCheckinRoute = ({ totalSteps, handleCompletePreCheckin, handleCancelPreCheckin }) => {
    const { booking, preCheckSettings } = useContext(ApiContext);
    const requiredIndex = getParticipantStepCount(booking, preCheckSettings);
    return (
        <StepWrapper requiredStepIndex={requiredIndex} totalSteps={totalSteps}>
            <PreCheckinStep
                onComplete={handleCompletePreCheckin}
                onCancel={handleCancelPreCheckin}
            />
        </StepWrapper>
    );
};

const SuccessRoute = ({ totalSteps }) => {
    const { booking, preCheckSettings } = useContext(ApiContext);
    const requiredIndex = getParticipantStepCount(booking, preCheckSettings) + 1; // Index after pre-checkin
    return (
        <StepWrapper requiredStepIndex={requiredIndex} totalSteps={totalSteps}>
            <SuccessStep />
        </StepWrapper>
    );
};

const HomeSignOutRedirect = () => {
    const { logout } = useContext(ApiContext);
    useEffect(() => {
        logout();
    }, [logout]);
    return null;
};

export default function Layout() {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const { tourOperator, booking, fetchBookingDetails, isLoading, error, preCheckSettings } = useContext(ApiContext);
    const { logout } = useContext(ApiContext);
    const navigate = useNavigate();
    const location = useLocation(); // Get current location for progress bar

    // Track overall completion status (needed for redirect/success step)
    const [isPreCheckinCompleted, setIsPreCheckinCompleted] = useState(false);

    // Check if pre-check-in is enabled
    const isPreCheckInEnabled = preCheckSettings?.enabled !== false; // Default to true if not specified

    const handleSignOut = () => {
        logout();
        navigate('/'); // Navigate to sign-in page after logout
    };

    // Handlers now use navigate
    const handleSaveAndContinue = (currentParticipantIndex) => {
        const nextIndex = currentParticipantIndex + 1;
        const participantSteps = getParticipantStepCount(booking, preCheckSettings);
        if (booking && nextIndex < participantSteps) {
            navigate(`/participant/${nextIndex}`);
        } else {
            navigate('/checkin'); // Move to Pre-Check-In step URL
        }
    };

    const handleCompletePreCheckin = () => {
        setIsPreCheckinCompleted(true); // Set completion flag
        navigate('/success'); // Navigate to Success step URL
    };

    const handleCancelPreCheckin = () => {
        const participantSteps = getParticipantStepCount(booking, preCheckSettings);
        const lastParticipantIndex = participantSteps - 1;
        navigate(`/participant/${lastParticipantIndex >= 0 ? lastParticipantIndex : 0}`);
    };

    const totalSteps = booking ? getParticipantStepCount(booking, preCheckSettings) + 1 : 0;
    const getCurrentStep = () => {
        if (!booking) return 0;
        const path = location.pathname;
        if (path === '/success') return totalSteps; // Return totalSteps to indicate completion, not totalSteps - 1
        if (path === '/checkin') return totalSteps - 1;
        const match = path.match(/\/participant\/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    };

    const currentStep = getCurrentStep();
    const isSuccessStep = location.pathname === '/success';
    const drawer = booking && isPreCheckInEnabled ? (
        <Sidebar currentStep={currentStep} isSuccessStep={isSuccessStep} />
    ) : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: theme.zIndex.drawer + 1
                }}
            >
                <Toolbar sx={{ justifyContent: 'center' }}> {/* Center content */}
                    <Box sx={{
                        maxWidth: 966,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: { xs: 1, lg: 0 }
                    }}>
                        {/* Logo */}
                        <TourOperatorBrand />
                        
                        {/* Controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LanguageSelector />
                            <Button
                                variant="text"
                                onClick={handleSignOut}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            // startIcon={<LogoutIcon />} // Optional icon
                            >
                                SIGN OUT
                            </Button>
                        </Box>
                    </Box>
                </Toolbar>
                {/* Mobile progress bar */}
                {!isDesktop && booking && totalSteps > 0 && !isSuccessStep && (
                    <LinearProgress
                        variant="determinate"
                        // Progress based on current step index relative to total steps (participants + precheckin)
                        value={Math.min(100, (currentStep / totalSteps) * 100)}
                        sx={{ height: 4, position: 'absolute', bottom: 0, width: '100%' }}
                    />
                )}
            </AppBar>

            <Container sx={{
                maxWidth: { lg: 966 }, // Ensure container respects max width
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                flexGrow: 1,
                // Adjust padding top based on desktop/mobile header height
                pt: { xs: `calc(${appBarHeight} + 4px)`, lg: `calc(${appBarHeight} + ${theme.spacing(4)})` },
                pb: theme.spacing(4), // Increased bottom padding
                px: { xs: 1, lg: 0 }, // Keep horizontal padding adjustments
                gap: { xs: 2, lg: 3 }, // Consistent gap
                alignItems: { xs: 'stretch', lg: 'flex-start' }, // Align items top on large screens
            }}>
                {/* Sidebar container - only show when pre-check-in is enabled */}
                {booking && isPreCheckInEnabled && (
                    <Box
                        component="nav"
                        sx={{
                            width: { xs: '100%', lg: drawerWidth },
                            flexShrink: { lg: 0 },
                            // Use Box for mobile layout instead of rendering Sidebar directly here
                            display: { xs: 'block', lg: 'block' }, // Ensure nav container is always block
                            mb: { xs: 2, lg: 0 } // Margin bottom only on mobile
                        }}
                        aria-label="pre-check-in steps"
                    >
                        {/* Conditional rendering for Drawer based on screen size */}
                        {isDesktop ? (
                            <Drawer
                                variant="permanent"
                                open
                                sx={{
                                    '& .MuiDrawer-paper': {
                                        boxSizing: 'border-box',
                                        width: drawerWidth,
                                        borderRight: 'none',
                                        boxShadow: theme.shadows[2],
                                        backgroundColor: 'background.paper',
                                        borderRadius: 1,
                                        position: 'relative', // Important for layout flow
                                        overflow: 'hidden', // Prevent scrolling at drawer level
                                    },
                                }}
                            >
                                {drawer}
                            </Drawer>
                        ) : (drawer)}
                    </Box>
                )}

                {/* Main content area */}
                <MainContent
                    sx={{
                        width: { 
                            xs: '100%', 
                            lg: booking && isPreCheckInEnabled 
                                ? `calc(100% - ${drawerWidth}px - ${theme.spacing(3)})` 
                                : '100%'
                        },
                        padding: theme.spacing(isDesktop ? 2 : 1)
                    }}
                >
                    {isLoading && !booking && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />}
                    {error && <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>Error loading booking: {error.message || String(error)}</Typography>}
                    {booking && !isPreCheckInEnabled && <PreCheckDisabled />}
                    {booking && isPreCheckInEnabled && (
                        <Routes>
                            <Route
                                path="/participant/:participantIndex"
                                element={<ParticipantRoute totalSteps={totalSteps} handleSaveAndContinue={handleSaveAndContinue} />}
                            />
                            <Route
                                path="/checkin"
                                element={<PreCheckinRoute totalSteps={totalSteps} handleCompletePreCheckin={handleCompletePreCheckin} handleCancelPreCheckin={handleCancelPreCheckin} />}
                            />
                            <Route
                                path="/success"
                                element={<SuccessRoute totalSteps={totalSteps} />}
                            />
                            {/* Home: clear session so manager testing links sees sign-in form instead of previous participant */}
                            <Route path="/" element={<HomeSignOutRedirect />} />
                            {/* Catch-all for invalid paths within the layout */}
                            <Route path="*" element={<Navigate to="/participant/0" replace />} />
                        </Routes>
                    )}
                    {/* Render fallback if booking is null and not loading/error */}
                    {!isLoading && !error && !booking && (
                        <Typography sx={{ mt: 4, textAlign: 'center' }}>No booking loaded.</Typography>
                    )}
                </MainContent>
            </Container>
        </Box>
    );
}
